import { Box } from "@mui/material";
import { useCallback, useReducer, useRef, useState } from "react"
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import useDialogModal from "../../../../hooks/useDialogModal";
import AddNewInvoiceDialog from "./newInvoice/addNewInvoiceDialog";
import { InvoiceService } from "../../../../service/invoiceService";
import useAbortController from "../../../../hooks/useAbortController";
import { isLocalDev } from "../../../../utils/environment";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { getInvoicesListColumns } from "./columns";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import validLookup from "../../../../utils/validLookup";
import { toast } from "react-toastify";
import EditInvoiceDialog from "./newInvoice/editInvoiceDialog";
import ReturnInvoiceDialog from "./newInvoice/returnInvoiceDialog";
import { DEFAULT_PAGE_SIZE } from "utils";
import { usePreferences } from "hooks/usePreferences";
import AddTaskDialog from "../../../task/AddTaskDialog";
import { useCan } from "hooks/useCan";
import FrameDispositionDialog from "./newInvoice/FrameDispositionDialog";

export const FRAME_HANDLING_PATIENT_OWN = 'patient own'
export const FRAME_HANDLING_STOCK = 'stock'
export const FRAME_HANDLING_ORDER_NEW = 'order new'

export default function InvoiceTab({ customer }) {
    const can = useCan();
    const canCreateInvoice = can.section('invoice', {action: 'create-invoice'});
    const canDeleteInvoice = can.section('invoice', {action: 'delete-invoice'});

    const { preferences } = usePreferences();
    const [signal] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
        showTaskDialog: false,
        taskTarget: null,
    });
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [NewInvoiceDialog, showNewInvoiceDialog] = useDialogModal(AddNewInvoiceDialog);
    const [EditInvoiceDialogComp, showEditInvoiceDialogComp] = useDialogModal(EditInvoiceDialog);
    const [ReturnInvoiceDialogComp, showReturnInvoiceDialogComp] = useDialogModal(ReturnInvoiceDialog);
    const [frameDispositionDialog, setFrameDispositionDialog] = useState({
        open: false,
        previews: [],
        title: 'Frame Handling Required',
        confirmLabel: 'Continue',
        inProgress: false,
    });
    const frameDispositionResolverRef = useRef(null);

    const openReturnInvoiceDialog = useCallback((invoiceId) => {
        if (!invoiceId) {
            return;
        }
        showReturnInvoiceDialogComp(null, { invoiceData: { id: invoiceId } });
    }, [showReturnInvoiceDialogComp]);

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        //updateState({ loading: true });
        InvoiceService.getInvoicesForCustomerWithLimit(from, pageSize, filters, sortBy, customer.customer.id, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            //updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            //updateState({ loading: false });
        });
    }, [customer.customer.id, signal]);

    const openFrameDispositionDialog = (previews, dialogOptions = {}) => {
        return new Promise((resolve) => {
            frameDispositionResolverRef.current = resolve;
            setFrameDispositionDialog({
                open: true,
                previews,
                title: dialogOptions.title || 'Frame Handling Required',
                confirmLabel: dialogOptions.confirmLabel || 'Continue',
                inProgress: false,
            });
        });
    };

    const closeFrameDispositionDialog = () => {
        setFrameDispositionDialog((prev) => ({ ...prev, open: false, inProgress: false }));
        if (frameDispositionResolverRef.current) {
            frameDispositionResolverRef.current(null);
            frameDispositionResolverRef.current = null;
        }
    };

    const confirmFrameDispositionDialog = (decisionsByInvoice) => {
        setFrameDispositionDialog((prev) => ({ ...prev, open: false, inProgress: false }));
        if (frameDispositionResolverRef.current) {
            frameDispositionResolverRef.current(decisionsByInvoice || null);
            frameDispositionResolverRef.current = null;
        }
    };

    const performDelete = async (invoiceIds, options = {}) => {
        const decisionsByInvoice = options.decisionsByInvoice || {};
        const requireDispositionDecisions = Boolean(options.requireDispositionDecisions);

        const deleteRecordResult = requireDispositionDecisions
            ? await InvoiceService.deleteInvoiceWithDisposition(invoiceIds, decisionsByInvoice, true)
            : await InvoiceService.deleteInvoice(invoiceIds);

        const { code, message } = deleteRecordResult;

        if (code === 0) {
            toast.success(message);
            const updateList = state.data.filter(d => !invoiceIds.includes(d.id));
            updateState({ data: updateList });
            return;
        }

        toast.error("Failed to delete record(s)");
    };

    const handleDeleteClicked = async (rowids) => {
        const invoiceIds = rowids.map((x) => parseInt(x, 10));

        let previews = [];
        let decisionsByInvoice = {};
        let requireDispositionDecisions = false;

        try {
            const previewResponses = await Promise.all(invoiceIds.map((invoiceId) => InvoiceService.getDispositionPreview(invoiceId)));
            previews = previewResponses
                .map((response) => {
                    if (typeof response?.code === 'number' && response.code !== 0) {
                        return null;
                    }
                    return response?.data || null;
                })
                .filter(Boolean);

            requireDispositionDecisions = previews.some((preview) => (preview.decisions || []).some((item) => item?.requiresDecision));

            if (requireDispositionDecisions) {
                const decisionResult = await openFrameDispositionDialog(previews, {
                    title: 'Delete Invoices: Frame Handling',
                    confirmLabel: 'Apply & Continue',
                });

                if (!decisionResult) {
                    return;
                }
                decisionsByInvoice = decisionResult;
            }
        } catch (error) {
            toast.error('Unable to load frame disposition details.');
            if (isLocalDev) {
                console.error('Disposition preview error:', error);
            }
            return;
        }

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    await performDelete(invoiceIds, {
                        decisionsByInvoice,
                        requireDispositionDecisions,
                    });
                } catch (error) {
                    toast.error("An error occurred while deleting records");
                    if (isLocalDev) {
                        console.error("Error: ", error);
                    }
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${invoiceIds.length} record(s)?`,
        });
    }

    const handleAddTask = (rowData) => {
        updateState({ taskTarget: rowData, showTaskDialog: true });
    };

    const handleRowEdit = (rowData) => {
        if (String(rowData?.status || '').toLowerCase() === 'refund') {
            openReturnInvoiceDialog(rowData.id);
            return;
        }

        showEditInvoiceDialogComp(null, {
            invoiceId: rowData.id,
            onOpenReturnInvoice: openReturnInvoiceDialog,
            onRefresh: () => {
                // Refresh the invoice list by re-fetching current page
                const currentPage = Math.floor(state.data.length > 0 ? 0 : 0); // You might want to track current page
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
            }
        });
    }

    const handleAddRecord = () => {
        if (canCreateInvoice) {
            showNewInvoiceDialog();
        } else {
            toast.error("You do not have permission to create a new invoice.");
        }
    }

    return (
        <Box display="flex" flexDirection="column">

            <Box display="flex" flexDirection="column">
                <EnhancedTable
                    columns={getInvoicesListColumns(handleRowEdit, preferences)}
                    addRecordHandler={canCreateInvoice ? () => handleAddRecord() : undefined}
                    deleteRecordHandler={canDeleteInvoice ? handleDeleteClicked : undefined}
                    data={state.data}
                    totalRowCount={state.totalRowCount}
                    pageCount={state.pageCount}
                    fetchData={fetchData}
                    loading={state.loading}
                    selectionMode="multi"
                    actions={[
                        {
                            icon: <EditIcon />,
                            onClick: handleRowEdit,
                        },
                        {
                            icon: <PlaylistAddCheckIcon />,
                            onClick: handleAddTask,
                        },
                    ]}
                />
                {/* <InsurancePolicyEditDialog customer={customer} onRecordEdit={recordEditHandler} /> */}

            </Box>
            <NewInvoiceDialog ignoreBackdropClick={true} customer={customer} onRefresh={() => {
                // Refresh the invoice list by re-fetching current page
                const currentPage = Math.floor(state.data.length > 0 ? 0 : 0);
                fetchData({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: currentPage, filters: [], sortBy: [] });
            }} />
            <EditInvoiceDialogComp ignoreBackdropClick={true} />
            <ReturnInvoiceDialogComp ignoreBackdropClick={true} customer={customer} />
            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => updateState({ showTaskDialog: false, taskTarget: null })}
                entityType="invoice"
                entityId={state.taskTarget?.id || null}
                onTaskCreated={() => updateState({ showTaskDialog: false })}
            />
            <FrameDispositionDialog
                open={frameDispositionDialog.open}
                previews={frameDispositionDialog.previews}
                title={frameDispositionDialog.title}
                confirmLabel={frameDispositionDialog.confirmLabel}
                onClose={closeFrameDispositionDialog}
                onConfirm={confirmFrameDispositionDialog}
                inProgress={frameDispositionDialog.inProgress}
            />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}